import React, { useEffect, useState } from "react";
import edit from "../assets/images/navbar/edit.png";
import deleteIocn from "../assets/images/cuisines/delete.png";
import walletIocn from "../assets/images/cuisines/wallet.png";
import star from "../assets/images/navbar/star.png";
import orangeStar from "../assets/images/navbar/orange-star.png";
import food from "../assets/images/order/food.png"

interface DishCardProps {
  id: string;
  image?: string;
  name: string;
  rating?: number;
  review?: number;
  price: number;
  salesCount: number;
  isAvailable?: boolean;
  onToggleAvailability?: (id: string, available: boolean) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onDynamicPricing?: (id: string) => void;
}

const DishCard: React.FC<DishCardProps> = ({
  id,
  image,
  name,
  rating,
  review,
  price,
  salesCount,
  isAvailable = true,
  onToggleAvailability,
  onEdit,
  onDelete,
  onDynamicPricing,
}) => {
  const [available, setAvailable] = useState(isAvailable);
  const ratingValue = rating ?? 0;

  useEffect(() => {
    setAvailable(isAvailable);
  }, [isAvailable]);

  const handleToggle = () => {
    const newState = !available;
    setAvailable(newState);
    onToggleAvailability?.(id, newState);
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => (
      <img
        key={index}
        src={index < ratingValue ? orangeStar : star}
        alt={index < ratingValue ? "filled star" : "empty star"}
        className="h-[14px] w-[14px] dark:invert"
      />
    ));
  };

  return (
    <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-xl p-3 md:p-4 mb-3 shadow-sm hover:shadow-md transition-all">
      {/* Mobile & Desktop Layout */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
        {/* Top Section - Image, Info, and Toggle */}
        <div className="flex items-start md:items-center justify-between gap-3 md:gap-4 w-full md:w-1/2">
          {/* Image and Info */}
          <div className="flex items-start gap-3 flex-1">
            <img
              src={image || food}
              alt={name}
              className="w-16 h-16 md:w-20 md:h-20 rounded-lg md:rounded-xl object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h3 className="text-[#050505] dark:text-gray-200 font-Urbanist text-[16px] md:text-[22.083px] font-medium leading-tight md:leading-[22.083px] mb-1">
                {name}
              </h3>
              <div className="flex items-center gap-2 md:gap-[14px] mb-1 flex-wrap">
                <span className="text-[#FF8A00] font-Urbanist text-[14px] md:text-[16px] font-medium">
                  ₹{price}
                </span>
                <div className="flex items-center gap-1.5">
                  <div className="text-[11px] md:text-[13px] font-urbanist font-normal text-[#6A6A6A] dark:text-gray-400">
                    <span>{ratingValue || 0}</span>/5
                  </div>
                  <div className="flex items-center gap-0.5">{renderStars()}</div>
                </div>
              </div>
              <p className="text-[#050505] dark:text-gray-300 font-Urbanist text-[12px] md:text-[14px] font-normal">
                {salesCount} Sales in this week
              </p>
            </div>
          </div>

          {/* Available Toggle */}
          <div className="flex flex-col md:flex-row items-end md:items-center gap-0.5 md:gap-1 flex-shrink-0">
            <span className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[9px] md:text-[10px] font-normal md:order-1">
              Available
            </span>
            <label className="relative inline-flex items-center cursor-pointer md:order-2">
              <input
                type="checkbox"
                checked={available}
                onChange={handleToggle}
                className="sr-only peer"
              />
              <div className="w-10 h-5 md:w-11 md:h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 md:after:h-5 md:after:w-5 after:transition-all peer-checked:bg-[#FF8A00]"></div>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex md:flex-col gap-2 justify-between md:justify-start md:min-w-[177px]">
          <button
            onClick={() => onEdit?.(id)}
            className="flex justify-center items-center gap-1 px-3 py-2 md:py-1.5 bg-[#F5F5F5] dark:bg-gray-700 hover:bg-[#EEEEEE] dark:hover:bg-gray-600 rounded text-[#050505] dark:text-gray-200 font-Urbanist text-[11px] md:text-[12px] font-normal transition-colors flex-1 md:flex-none"
          >
            <img src={edit} alt="Edit" className="h-[11px] w-[11px] md:h-[12px] md:w-[12px] dark:invert" />
            Edit
          </button>
          <button
            onClick={() => onDelete?.(id)}
            className="flex justify-center items-center gap-1 px-3 py-2 md:py-1.5 bg-[#F5F5F5] dark:bg-gray-700 hover:bg-[#EEEEEE] dark:hover:bg-gray-600 rounded text-[#050505] dark:text-gray-200 font-Urbanist text-[11px] md:text-[12px] font-normal transition-colors flex-1 md:flex-none"
          >
            <img src={deleteIocn} alt="Delete" className="h-[11px] w-[11px] md:h-[12px] md:w-[12px] dark:invert" />
            Delete
          </button>
          {/* <button
            onClick={() => onDynamicPricing?.(id)}
            className="flex justify-center items-center gap-1 px-3 py-2 md:py-1.5 bg-[#F5F5F5] hover:bg-[#EEEEEE] rounded text-[#050505] font-Urbanist text-[11px] md:text-[12px] font-normal transition-colors flex-1 md:flex-none whitespace-nowrap"
          >
            <img
              src={walletIocn}
              alt="Dynamic Pricing"
              className="h-[11px] w-[11px] md:h-[12px] md:w-[12px]"
            />
            <span className="hidden sm:inline">Dynamic Pricing</span>
            <span className="sm:hidden">Pricing</span>
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default DishCard;
